import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  getUserLessons,
  getUserPrompts,
  // getUserLesson, // Used by progressService internally
  // getUserPrompt, // Used by progressService internally  
  updateUserLesson,
  updateUserPrompt,
  userHasProgress,
  getUserLevel,
  getUserCourse,
  getUserLevelsForCourse,
  getUserCourses,
  getMostRecentCourse
} from '../services/progressService';

/**
 * Progress tracking system for learning content
 * 
 * Core values:
 * - SIMPLE: Single source of truth for all progress data
 * - EFFICIENT: Batch updates, minimal re-renders, Firebase caching
 * - SCALABLE: Works with any content type (lessons, prompts, future content)
 */

const ProgressContext = createContext();

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export const ProgressProvider = ({ children }) => {
  const { user, userRole, loading: authLoading } = useAuth();
  const [userLessonsData, setUserLessonsData] = useState({});
  const [userPromptsData, setUserPromptsData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Load user's progress from new 4-collection structure
  useEffect(() => {
    const loadUserProgress = async () => {
      // Don't load progress until auth is fully loaded and user is authenticated
      if (authLoading || !user || !userRole) {
        setIsLoading(authLoading);
        return;
      }

      setIsLoading(true);
      try {
        // Check if user has new-format progress data
        let hasNewProgress = false;
        try {
          hasNewProgress = await userHasProgress(user.uid);
        } catch (serviceError) {
          console.error('userHasProgress failed:', serviceError);
          hasNewProgress = false;
        }
        
        if (hasNewProgress) {
          // Load from new collections
          const [lessonsData, promptsData] = await Promise.all([
            getUserLessons(user.uid),
            getUserPrompts(user.uid)
          ]);
          
          setUserLessonsData(lessonsData);
          setUserPromptsData(promptsData);
        } else {
          // Check for old format data to migrate
          await migrateOldProgressData(user.uid);
        }
      } catch (error) {
        console.error('Error loading progress:', error);
        // Fallback to localStorage if Firebase fails
        await loadFromLocalStorageFallback(user.uid);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProgress();
  }, [authLoading, user, userRole]); // Depend on auth state

  // Migration helper functions
  const migrateOldProgressData = async (userId) => {
    try {
      // Try old Firebase document first
      const docRef = doc(db, 'user_progress', userId);
      const docSnap = await getDoc(docRef);
      
      let oldProgressData = null;
      
      if (docSnap.exists()) {
        oldProgressData = docSnap.data();
        // Migrate old Firebase progress data
      } else {
        // Try localStorage
        const savedProgressData = localStorage.getItem(`progress_${userId}`);
        if (savedProgressData) {
          oldProgressData = JSON.parse(savedProgressData);
          // Migrate localStorage progress data
        }
      }
      
      if (oldProgressData) {
        await migrateProgressToNewCollections(userId, oldProgressData);
        
        // Clean up old data after successful migration
        if (localStorage.getItem(`progress_${userId}`)) {
          localStorage.removeItem(`progress_${userId}`);
        }
      }
    } catch (error) {
      console.error('Error during migration:', error);
    }
  };

  const migrateProgressToNewCollections = async (userId, oldProgressData) => {
    try {
      const migrationPromises = [];
      
      // Migrate each piece of progress data
      Object.entries(oldProgressData).forEach(([contentId, progressData]) => {
        if (progressData && typeof progressData === 'object') {
          // Determine if this is a lesson or prompt based on content structure
          // Better classification: prompts have response field, lessons have todos/completion
          if (progressData.response !== undefined) {
            // Looks like prompt data
            migrationPromises.push(
              updateUserPrompt(userId, contentId, progressData)
            );
          } else {
            // Looks like lesson data
            migrationPromises.push(
              updateUserLesson(userId, contentId, progressData)
            );
          }
        }
      });
      
      await Promise.all(migrationPromises);
      
      // Reload data after migration
      const [lessonsData, promptsData] = await Promise.all([
        getUserLessons(userId),
        getUserPrompts(userId)
      ]);
      
      setUserLessonsData(lessonsData);
      setUserPromptsData(promptsData);
      
    } catch (error) {
      console.error('Error migrating progress data:', error);
      throw error;
    }
  };

  const loadFromLocalStorageFallback = async (userId) => {
    try {
      const savedProgressData = localStorage.getItem(`progress_${userId}`);
      if (savedProgressData) {
        const localData = JSON.parse(savedProgressData);
        // Using localStorage fallback
        
        // Convert old format to new state structure for local use
        const lessonsData = {};
        const promptsData = {};
        
        Object.entries(localData).forEach(([contentId, progressData]) => {
          if (progressData && typeof progressData === 'object') {
            if (progressData.response !== undefined) {
              promptsData[contentId] = progressData;
            } else {
              lessonsData[contentId] = progressData;
            }
          }
        });
        
        setUserLessonsData(lessonsData);
        setUserPromptsData(promptsData);
      }
    } catch (error) {
      console.error('Error loading from localStorage fallback:', error);
      setUserLessonsData({});
      setUserPromptsData({});
    }
  };

  // Update content progress - automatically determines if lesson or prompt
  const updateContentProgress = (contentId, progressData, type = null) => {
    if (!user) return;
    
    // Optimistic update - update state immediately for responsive UI
    // Classification logic:
    let isPrompt = false;
    if (type === 'prompt') {
      isPrompt = true;
    } else if (type === 'lesson') {
      isPrompt = false;
    } else {
      // Auto-detect: check if this ID already exists in prompt data, or if it comes from prompt context
      isPrompt = userPromptsData[contentId] !== undefined;
    }
    
    if (isPrompt) {
      setUserPromptsData(prev => ({
        ...prev,
        [contentId]: {
          ...prev[contentId],
          ...progressData,
          lastUpdated: new Date().toISOString()
        }
      }));
    } else {
      setUserLessonsData(prev => ({
        ...prev,
        [contentId]: {
          ...prev[contentId],
          ...progressData,
          lastUpdated: new Date().toISOString()
        }
      }));
    }
    
    // Background Firebase update (don't block UI)
    const updateFirebase = async () => {
      try {

        if (isPrompt) {
          await updateUserPrompt(user.uid, contentId, progressData);

        } else {
          await updateUserLesson(user.uid, contentId, progressData);

        }
      } catch (error) {
        console.error('❌ Error updating content progress in Firebase:', error);
        // Fallback to localStorage
        const allProgressData = { ...userLessonsData, ...userPromptsData };
        allProgressData[contentId] = {
          ...allProgressData[contentId],
          ...progressData,
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem(`progress_${user.uid}`, JSON.stringify(allProgressData));
      }
    };
    
    // Fire and forget - don't await
    updateFirebase();
  };

  // Get content progress from combined data
  const getContentProgress = (contentId) => {
    return userLessonsData[contentId] || userPromptsData[contentId] || null;
  };

  // Get content status from combined data
  const getContentStatus = (contentId) => {
    const contentProgress = getContentProgress(contentId);
    return contentProgress?.status || 'not_started';
  };

  // Boolean checks for common status queries
  const isContentCompleted = (contentId) => {
    const contentProgress = getContentProgress(contentId);
    return contentProgress?.completed === true || getContentStatus(contentId) === 'completed';
  };

  const isContentActive = (contentId) => {
    return getContentStatus(contentId) === 'active';
  };

  const isContentNotStarted = (contentId) => {
    return getContentStatus(contentId) === 'not_started';
  };


  // SCALABLE: Level progress calculation for course organization
  const calculateLevelProgress = (levelNumber, courseLessons) => {
    if (!courseLessons || courseLessons.length === 0) return null;
    
    // EFFICIENT: Filter once, use multiple times
    const lessonsInLevel = courseLessons.filter(lesson => lesson.level === levelNumber);
    if (lessonsInLevel.length === 0) return null;
    
    // SIMPLE: Count completed and find active lesson
    const completedLessonsInLevel = lessonsInLevel.filter(lesson => isContentCompleted(lesson.id));
    const activeLessonInLevel = lessonsInLevel.find(lesson => isContentActive(lesson.id));
    
    const totalLessons = lessonsInLevel.length;
    const completedCount = completedLessonsInLevel.length;
    const levelIsCompleted = completedCount === totalLessons && totalLessons > 0;
    
    // EFFICIENT: Calculate states once
    const levelIsActive = (activeLessonInLevel != null) && !levelIsCompleted;
    const levelIsNotStarted = (activeLessonInLevel == null) && completedCount === 0;
    
    return {
      total: totalLessons,
      completed: completedCount,
      current: levelIsActive && activeLessonInLevel ? activeLessonInLevel.lesson_number : null,
      isCompleted: levelIsCompleted,
      isActive: levelIsActive,
      isNotStarted: levelIsNotStarted
    };
  };

  // EFFICIENT: Smart routing to find the right lesson for user's current progress
  const findNextAccessibleLesson = (courseId, courseLessons) => {
    if (!courseLessons || courseLessons.length === 0) return null;
    
    // SIMPLE: Sort lessons once for consistent navigation
    const lessonsInOrder = [...courseLessons].sort((lessonA, lessonB) => {
      if (lessonA.level !== lessonB.level) return lessonA.level - lessonB.level;
      return lessonA.lesson_number - lessonB.lesson_number;
    });

    // EFFICIENT: First priority - find active lesson (user's current work)
    const currentActiveLesson = lessonsInOrder.find(lesson => isContentActive(lesson.id));
    if (currentActiveLesson) return currentActiveLesson;

    // SCALABLE: Second priority - find first accessible unstarted lesson
    for (const lesson of lessonsInOrder) {
      const lessonStatus = getContentStatus(lesson.id);
      
      if (lessonStatus === 'not_started') {
        // Check if lesson can be accessed based on sequential completion
        if (canAccessContent(lesson.id, courseLessons)) {
          return lesson;
        }
      }
    }

    // SIMPLE: If all completed, return first lesson for review
    if (lessonsInOrder.length > 0 && lessonsInOrder.every(lesson => isContentCompleted(lesson.id))) {
      return lessonsInOrder[0];
    }
    
    return null;
  };

  // EFFICIENT: Course-level status calculation
  const calculateCourseStatus = (courseId, courseLessons) => {
    if (!courseLessons || courseLessons.length === 0) return 'not_started';
    
    // SIMPLE: Check lesson states once
    const hasActiveLessons = courseLessons.some(lesson => isContentActive(lesson.id));
    const hasCompletedLessons = courseLessons.some(lesson => isContentCompleted(lesson.id));
    const allLessonsCompleted = courseLessons.every(lesson => isContentCompleted(lesson.id));
    
    if (allLessonsCompleted) {
      return 'completed';
    } else if (hasActiveLessons || hasCompletedLessons) {
      return 'in_progress';
    } else {
      return 'not_started';
    }
  };

  // SCALABLE: Sequential access control for any ordered content
  const canAccessContent = (targetContentId, allContentInOrder) => {
    if (!allContentInOrder || allContentInOrder.length === 0) return false;
    
    // EFFICIENT: Find target content once
    const targetContent = allContentInOrder.find(content => content.id === targetContentId);
    if (!targetContent) return false;
    
    // SIMPLE: Sort content by intended order
    const contentInOrder = [...allContentInOrder].sort((contentA, contentB) => {
      if (contentA.level !== contentB.level) return contentA.level - contentB.level;
      return contentA.lesson_number - contentB.lesson_number;
    });
    
    // EFFICIENT: Find position once
    const targetPosition = contentInOrder.findIndex(content => content.id === targetContentId);
    if (targetPosition === -1) return false;
    
    // SIMPLE: First content is always accessible
    if (targetPosition === 0) return true;
    
    // SCALABLE: Check all prerequisite content is completed
    for (let i = 0; i < targetPosition; i++) {
      if (!isContentCompleted(contentInOrder[i].id)) {
        return false;
      }
    }
    
    return true;
  };

  // Export API with backward compatibility
  const progressAPI = {
    // Loading state
    isLoading,
    
    // New clean API
    updateContentProgress,
    getContentProgress, 
    getContentStatus,
    isContentCompleted,
    isContentActive,
    isContentNotStarted,
    calculateLevelProgress,
    findNextAccessibleLesson,
    calculateCourseStatus,
    canAccessContent,
    
    // Access to computed progress
    getUserLevel: (courseId, levelNumber) => getUserLevel(user?.uid, courseId, levelNumber),
    getUserCourse: (courseId) => getUserCourse(user?.uid, courseId),
    getUserLevelsForCourse: (courseId) => getUserLevelsForCourse(user?.uid, courseId),
    getUserCourses: () => getUserCourses(user?.uid),
    getMostRecentCourse: () => getMostRecentCourse(user?.uid),
    
    // Backward compatibility aliases - maintain old API
    progress: { ...userLessonsData, ...userPromptsData }, // Combined for compatibility
    updateProgress: updateContentProgress,
    getLessonProgress: getContentProgress,
    getLessonStatus: getContentStatus,
    isLessonCompleted: isContentCompleted,
    isLessonActive: isContentActive,
    isLessonNotStarted: isContentNotStarted,
    getLevelProgress: calculateLevelProgress,
    findNextIncompleteLesson: findNextAccessibleLesson,
    getCourseStatus: calculateCourseStatus,
    canAccessLesson: canAccessContent,
    
    // Raw data access (for debugging/migration)
    _userLessonsData: userLessonsData,
    _userPromptsData: userPromptsData
  };

  return (
    <ProgressContext.Provider value={progressAPI}>
      {children}
    </ProgressContext.Provider>
  );
};